/**
 * Black Friday tiers — banner + cart progress driver.
 *
 * Powers <bf-tier-bar>. Config comes from window.theme.bfTiers (snippets/bf-tiers-head).
 * Every bar starts hidden and is only revealed for shoppers who hold the code:
 *   - the code is on the cart (/cart.js discount_codes — present even below the
 *     first tier, with applicable:false), or
 *   - Shopify's `discount_code` cookie (set by /discount/CODE links), or
 *   - a ?discount= / ?discount_code= URL param (cart-components applies it).
 * A different code on the cart wins over the cookie, so a shopper who swapped
 * codes stops seeing these nudges.
 *
 * The spend that counts toward a tier excludes the configured product types (the
 * $1 accessory parts), so the bar never promises a tier checkout won't give.
 *
 * Cart totals follow window.theme.cart.store, like assets/qb-promo.js. Everything is
 * wrapped so a failure here leaves the bars hidden and never breaks the cart.
 */
(function () {
  var cfg = window.theme && window.theme.bfTiers;
  if (!cfg || !cfg.code) return;

  var CODE = String(cfg.code).trim().toLowerCase();
  var TIERS = (cfg.tiers || [])
    .filter(function (t) { return t && t.min > 0 && t.percent > 0; })
    .sort(function (a, b) { return a.min - b.min; });
  var EXCLUDED = (cfg.excludedTypes || [])
    .map(function (t) { return String(t).trim().toLowerCase(); })
    .filter(Boolean);
  if (!TIERS.length) return;

  var EVENT = 'bf:cart-updated';
  var lastCart = null;
  var lastCodes = null; // discount_codes survive store updates that omit them

  function root() {
    try {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (e) {
      return '/';
    }
  }

  function getStore() {
    try {
      var s = window.theme && window.theme.cart && window.theme.cart.store;
      return s && typeof s.subscribe === 'function' ? s : null;
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.money_format) || '';
        return window.Shopify.formatMoney(cents, fmt).replace(/[\.,]00$/, '');
      }
    } catch (e) {}
    var dollars = cents / 100;
    return '$' + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
  }

  function readCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function urlCode() {
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get('discount') || p.get('discount_code') || '';
    } catch (e) {
      return '';
    }
  }

  function holdsCode() {
    if (cfg.endsAt && Date.now() >= cfg.endsAt) return false;

    if (Array.isArray(lastCodes) && lastCodes.length) {
      return lastCodes.some(function (d) {
        return d && String(d.code || '').toLowerCase() === CODE;
      });
    }
    // Shopify's discount_code cookie can hold several comma-separated codes.
    var cookieCodes = readCookie('discount_code').toLowerCase().split(',');
    if (cookieCodes.some(function (c) { return c.trim() === CODE; })) return true;
    return urlCode().trim().toLowerCase() === CODE;
  }

  function eligibleSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.reduce(function (sum, item) {
      var type = String(item.product_type || '').trim().toLowerCase();
      if (EXCLUDED.indexOf(type) !== -1) return sum;
      var price = typeof item.original_line_price === 'number' ? item.original_line_price : item.line_price;
      return sum + (price || 0);
    }, 0);
  }

  function absorb(cart) {
    if (!cart || typeof cart !== 'object') return;
    lastCart = cart;
    if (Array.isArray(cart.discount_codes)) lastCodes = cart.discount_codes;
    try {
      // Drives the [data-bf-show] / [data-bf-hide] copy swaps (bf-tiers-head).
      document.documentElement.classList.toggle('bf-code', holdsCode());
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent(EVENT));
    } catch (e) {}
  }

  /* ---- data sources: one /cart.js read for discount_codes, then the store ---- */
  var started = false;
  function start(attempt) {
    if (started) return;
    var store = getStore();
    if (!store && attempt < 20) {
      setTimeout(function () { start(attempt + 1); }, 150);
      return;
    }
    started = true;
    if (store) {
      try {
        absorb(store.getState().products);
        store.subscribe(function (state) { absorb(state && state.products); });
      } catch (e) {}
    }
    fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(absorb)
      .catch(function () {});
  }

  /* ---- custom element ---- */
  class BfTierBar extends HTMLElement {
    connectedCallback() {
      try {
        this.layout = this.getAttribute('layout') || 'cart';
        this.msgEl = this.querySelector('[data-bf-msg]');
        this.fillEl = this.querySelector('[data-bf-fill]');
        this.stepsEl = this.querySelector('[data-bf-steps]');
        if (this.stepsEl && !this.stepsEl.children.length) this._buildSteps();

        this._onUpdate = () => this.render();
        document.addEventListener(EVENT, this._onUpdate);
        this.render();
        start(0);
      } catch (e) {}
    }

    disconnectedCallback() {
      try {
        if (this._onUpdate) document.removeEventListener(EVENT, this._onUpdate);
      } catch (e) {}
    }

    _buildSteps() {
      var html = TIERS.map(function (t) {
        return '<li class="bf-tier-bar__step"><b>' + t.percent + '% off</b>' + formatMoney(t.min) + '+</li>';
      }).join('');
      this.stepsEl.innerHTML = html;
    }

    render() {
      try {
        if (!holdsCode()) {
          this.hidden = true;
          return;
        }

        // Before the cart has loaded, the product banner can still show the ladder.
        var spend = eligibleSubtotal(lastCart);
        if (spend === null && this.layout !== 'product') return;
        spend = spend || 0;

        var current = null;
        var next = null;
        for (var i = 0; i < TIERS.length; i++) {
          if (spend >= TIERS[i].min) current = TIERS[i];
          else if (!next) next = TIERS[i];
        }

        var msg;
        if (next) {
          var add = '<strong>Add ' + formatMoney(next.min - spend) + '</strong> to save <strong>' + next.percent + '%</strong>';
          msg = current ? '<strong>' + current.percent + '% off</strong> unlocked · ' + add : add;
        } else {
          msg = '<strong>' + current.percent + '% off</strong> unlocked, our biggest saving, applied at checkout';
        }
        if (this.msgEl) this.msgEl.innerHTML = msg;

        var top = TIERS[TIERS.length - 1].min;
        var pct = Math.max(0, Math.min(100, Math.round((spend / top) * 100)));
        if (this.fillEl) this.fillEl.style.width = pct + '%';

        if (this.stepsEl) {
          Array.prototype.forEach.call(this.stepsEl.children, function (li, idx) {
            if (spend >= TIERS[idx].min) li.setAttribute('data-reached', '');
            else li.removeAttribute('data-reached');
          });
        }

        this.hidden = false;
      } catch (e) {}
    }
  }

  try {
    if (window.customElements && !customElements.get('bf-tier-bar')) {
      customElements.define('bf-tier-bar', BfTierBar);
    }
  } catch (e) {}

  // Collection promo card: end it level with the star rating of the product card
  // beside it. Lives here rather than inline because the theme re-renders the
  // grid (filters, paging), which drops inline scripts.
  var STARS = '.jdgm-preview-badge, .rating';
  function fitCards() {
    Array.prototype.forEach.call(document.querySelectorAll('.bf-card'), function (card) {
      var cell = card.closest('.o-layout__item');
      var next = cell && cell.nextElementSibling;
      var stars = next && next.querySelector(STARS);
      if (!stars || !card.offsetParent) return;
      var h = Math.round(stars.getBoundingClientRect().bottom - card.getBoundingClientRect().top);
      if (h > 120 && card.style.getPropertyValue('--bf-card-h') !== h + 'px') {
        card.style.setProperty('--bf-card-h', h + 'px');
        card.classList.add('bf-card--fit');
      }
    });
  }
  var fitQueued = false;
  function queueFit() {
    if (fitQueued) return;
    fitQueued = true;
    requestAnimationFrame(function () { fitQueued = false; fitCards(); });
  }
  try {
    window.addEventListener('resize', queueFit);
    window.addEventListener('load', queueFit);
    // Grid re-renders and late review badges both show up as DOM changes.
    new MutationObserver(queueFit).observe(document.documentElement, { childList: true, subtree: true });
    queueFit();
  } catch (e) {}

  // Pages with no bar (home, collections with an empty cart) still need the cart
  // read so the copy swaps follow the code actually on the cart.
  start(0);
})();
